/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import currencyFormatter from 'utils/currencyFormatter';
import CurrencyInput from 'components/CurrencyInput';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import ModalConfirmation from 'components/ModalConfirmation';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getNivelesSalarialesTabulares,
  postNivelSalarialTabular,
  deleteNivelSalarialTabular,
  patchNivelSalarialTabular,
  getNivelSalarialTabular,
} from 'api/nominas/catalogos/nivelesSalarialesTabulares';
import {
  SLUG_PATTERN,
  FLOAT_NUMER_PATTERN,
} from 'utils/patterns';
import { checkToken } from 'api/utils';
import {
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
} from '../../../utils/constants';

export const nivelSalarialTabularPermission = {
  permissionModel: 'nivelsalarialtabular',
  catalogsModels: [],
  tabsModels: [],
};

const NivelSalarialTabular = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getNivelesSalarialesTabulares();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getNivelesSalarialesTabulares(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setEstatusInactivo(false);
    setEstatusAutorizado(false);
    setDisableSave(false);
    setEstadosFiltered(estados);
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableSave(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _nivel = await getNivelSalarialTabular(match.id);
    if (_nivel?.estados_globales === ESTATUS_ACTIVO || _nivel?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO || estado?.id === ESTATUS_ACTIVO));
    }
    if (_nivel?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_nivel?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    setTimeout(() => {
      if (_nivel) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          isDisabled(_nivel?.estados_globales);
          form.setFieldsValue(formatReceived(_nivel));
          formDatos.setFieldsValue(formatReceived(_nivel));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchNivelSalarialTabular(key, values) : postNivelSalarialTabular(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteNivelSalarialTabular(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchNivelSalarialTabular(key, vals);
      if (res) {
        onSuccess(res);
        const _data = await getNivelesSalarialesTabulares();
        setData(formatReceived(_data));
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Sueldo Diario',
      dataIndex: 'sueldo_diario',
      key: 'sueldo_diario',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Sueldo Mensual',
      dataIndex: 'sueldo_mensual',
      key: 'sueldo_mensual',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    estatus: [requiredRule],
    clave: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    vigencia_inicial: [
      requiredRule,
      {
        validator: async () => {
          const { vigencia_inicial, vigencia_final } = form.getFieldsValue();
          if (vigencia_inicial && vigencia_final
            && vigencia_inicial.diff(vigencia_final, 'days') >= 0) {
            throw new Error('Este campo debe ser menor que la Vigencia Final');
          }
        },
      },
    ],
    vigencia_final: [
      requiredRule,
      {
        validator: async () => {
          const { vigencia_inicial, vigencia_final } = form.getFieldsValue();
          if (vigencia_inicial && vigencia_final
            && vigencia_final.diff(vigencia_inicial, 'days') <= 0) {
            throw new Error('Este campo debe ser mayor que la Vigencia Inicial');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          titleTable="Niveles Salariales Tabulares"
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableDelete={
            data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
            || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
          }
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'nivelsalarialtabular', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
            },
          ]}
          permission={permission}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Nivel Salarial Tabular`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableSave}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="45%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="clave"
                  rules={rules.clave}
                  label="Clave"
                  hasFeedback
                >
                  <Input
                    allowClear
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <CurrencyInput
                  name="sueldo_diario"
                  label="Sueldo Diario"
                  rules={rules.valor}
                  onChange={(value) => {
                    form.setFieldsValue({
                      sueldo_mensual: (value * 30).toFixed(2),
                    });
                  }}
                  disabled={estatusAutorizado || estatusInactivo}
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <CurrencyInput
                  name="sueldo_mensual"
                  label="Sueldo Mensual"
                  rules={rules.valor}
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_inicial"
                  label="Vigencia Inicial"
                  rules={rules.vigencia_inicial}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD/MM/YYYY"
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD/MM/YYYY"
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estadosFiltered}
                    disabled={!selectedRowKeys.length || estatusInactivo
                  || estatusAutorizado}
                    onChange={(val) => {
                      if (val === ESTATUS_INACTIVO) {
                        setConfirmationVisibleEstatus(true);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Nivel Salarial Tabular ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar el Nivel Salarial Tabular ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          ConfirmInactive
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          contentTitle={`Inactivar el Nivel Salarial Tabular ${form.getFieldValue('clave')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default NivelSalarialTabular;
